import { type Editor, EditorContent } from "@tiptap/react";
import { cva, type VariantProps } from "class-variance-authority";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { type Control, type FieldValues, type Path, useController } from "react-hook-form";
import type { TextareaProps } from "../../libs/tiptap";
import { useTiptap } from "../../libs/tiptap";
import type { DataAttributeProps } from "../../types";
import { cn } from "../../utils";
import { type ExtendedInputWrapperProps, InputWrapper } from "../input-wrapper";
import { EditorMenu } from "./editor-menu";

/** Props Interface */
export interface TextInputProps<T extends FieldValues>
	extends ExtendedInputWrapperProps<T>,
		TextareaProps,
		VariantProps<typeof textarea>,
		DataAttributeProps {
	placeholder?: string;
	control: Control<T>;
	name: Path<T>;
	className?: string;
	autoFocus?: boolean;
	onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
	onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
	onKeyUp?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
	isReadOnly?: boolean;
	menuType?: "static" | "floating";
	/**
	 * Where the formatting menu renders. Left out, it sits above the text. Given an element, it
	 * is rendered into that element instead — a page that keeps the toolbar in its header row,
	 * say. `null` means that element has not mounted yet, and no menu at all is better than one
	 * that flashes above the text for a frame.
	 */
	menuContainer?: HTMLElement | null;
	/**
	 * Hands the TipTap editor instance up once it exists, for a caller that inserts content
	 * from outside the editor chrome — a recording modal dropping its video block in.
	 */
	onEditorReady?: (editor: Editor | null) => void;
	/**
	 * The least height a `limited` box keeps while its text is shorter, in pixels. `0` makes an
	 * empty box one line tall, level with the single-line inputs beside it, and it grows with
	 * what is typed.
	 */
	minHeight?: number;
}

export function Textarea<T extends FieldValues>({
	control,
	name,
	members,
	projects,
	mentionRenderers,
	videoRenderer,
	imageRenderer,
	size,
	isReadOnly = false,
	disableFormatting = false,
	autoFocus,
	variant,
	className,
	menuType = "static",
	menuContainer,
	onKeyUp,
	onFocus,
	onBlur,
	onEditorReady,
	placeholder,
	isDisabled,
	minHeight = 100,
	...props
}: TextInputProps<T>) {
	/** State */
	const [_height, setHeight] = useState(0);

	/** Classes */
	const classes = textarea({ size, variant });

	/** Hooks */
	const ref = useRef<HTMLDivElement>(null);
	const controller = useController({
		control,
		name,
	});
	// Whether the author has been in the box. A reset puts the cursor back only then, so a form
	// that clears a field nobody has touched does not pull focus across the page.
	const hasBeenFocused = useRef(false);
	// The value the editor last handed to the form, so a change the form made can be told from
	// the echo of the editor's own edit — see the sync effect below.
	const lastEmittedValue = useRef<unknown>(controller.field.value);

	/** Editor */
	const { editor, handleOnGenerateContent } = useTiptap({
		placeholder,
		disableFormatting,
		isReadOnly,
		isDisabled,
		members,
		mentionRenderers,
		videoRenderer,
		imageRenderer,
		onChange: (next) => {
			lastEmittedValue.current = next;
			controller.field.onChange(next);
		},
		projects,
		value: controller.field.value,
		dataCy: props["data-cy"] || "textarea",
	});

	/** Effects */
	useEffect(() => {
		onEditorReady?.(editor);
	}, [editor]);

	// The box is as tall as what it holds, and it has to follow content that grows **after** the
	// document stopped changing. An editor update is the wrong signal on its own: a video block
	// lays out once its player has loaded, an image once it has decoded, and neither is a document
	// change — so a page with videos measured itself before they took their space and stayed at
	// that height, with the rest of the page behind a scrollbar inside a shallow box.
	useEffect(() => {
		const container = ref.current;
		if (!container) return;
		const content = container.querySelector(".ProseMirror");
		if (!content) return;

		const observer = new ResizeObserver(() => {
			setHeight(Math.round(content.clientHeight) + 16);
		});
		observer.observe(content);
		return () => observer.disconnect();
	}, [editor]);

	// The form's value reaches the editor only when the form changed it — the note form resetting
	// to "" after a send, a `setValue` from a caller — never as the echo of the editor's own edit,
	// which the form already holds. The echo matters because the editor reports `""` for every
	// document the value model calls empty while the author is still looking at it: a table of
	// empty cells, a heading with nothing typed yet, a cell whose last character was just deleted.
	// Writing that echo back as `setContent("")` replaces each of them with a blank paragraph.
	// Focus is no signal here either: the first focus of a box changes nothing in the form.
	useEffect(() => {
		if (!editor) return;
		const value = controller.field.value;
		if (value === lastEmittedValue.current) return;
		lastEmittedValue.current = value;
		// The same route the initial content takes, so a value set by the form renders as it would
		// on a fresh mount.
		editor.commands.setContent(value ? handleOnGenerateContent(value) : "", { emitUpdate: false });
		// A reset is the form handing the box back for the next entry, so the cursor returns — to
		// where the author already was, and nowhere else.
		if (!value && hasBeenFocused.current) editor.chain().focus().run();
	}, [controller.field.value, editor]);

	useEffect(() => {
		if (autoFocus && !hasBeenFocused.current) editor?.chain().focus().run();
	}, [autoFocus, editor]);

	const height = Math.max(_height, minHeight);

	// The toolbar is the box's own child so that it re-renders with the editor; a caller that
	// wants it elsewhere gets a portal of this one, not a second instance to keep in step.
	const menu =
		!isReadOnly && !disableFormatting ? (
			<EditorMenu
				menuType={menuType}
				editor={editor}
			/>
		) : null;

	/** Render */
	return (
		<InputWrapper
			{...props}
			errors={controller.formState.errors}
			className={cn(size === "full" && "flex-1 min-h-0")}
			onLabelClick={() => {
				editor?.chain().focus().run();
			}}
		>
			<div
				className={cn("w-full max-w-[100%] v-stack", size === "full" ? "flex-1 min-h-0" : "h-full")}
				data-cy={props["data-cy"] || "textarea"}
			>
				{menuContainer === undefined
					? menu
					: menuContainer && menu && createPortal(menu, menuContainer)}
				<EditorContent
					ref={ref}
					editor={editor}
					disabled={isDisabled}
					className={cn(
						"max-w-[100%] overflow-y-auto cursor-text",
						"relative",
						isReadOnly && !isDisabled && "!cursor-default",
						isDisabled && "input-disabled",
						className,
						classes,
					)}
					// A limited box is as tall as its content and lets the page scroll; a full one takes
					// the height its flex column gives it and scrolls inside, so whatever sits above it
					// stays where it is while the author edits far down the document.
					style={size === "full" ? undefined : { height }}
					// A click anywhere in the box focuses the editor, so the empty space beneath a short
					// text reaches the caret too. The handler sits on the box and never on the stack
					// above it, which also holds the toolbar: a toolbar click that bubbles into a focus
					// call dismisses the table menu — a non-modal dropdown that closes as soon as focus
					// leaves it — with the same click that opened it.
					onClick={() => {
						editor?.chain().focus().run();
					}}
					onFocus={(e) => {
						onFocus?.(e as any);
						hasBeenFocused.current = true;
					}}
					onBlur={(e) => {
						onBlur?.(e as any);
					}}
					onKeyUp={onKeyUp}
				/>
			</div>
		</InputWrapper>
	);
}

const textarea = cva([], {
	defaultVariants: {
		size: "limited",
		variant: "input",
	},
	variants: {
		size: {
			// `full` fills a flex column and scrolls within itself; the box, its wrapper and the
			// menu-plus-box stack all take `flex-1 min-h-0` for that, and no fixed height is set.
			full: ["flex-1 min-h-0"],
			limited: [],
		},
		variant: {
			input: ["input"],
			// No box at all: the wiki's document is typed where it is read, and only the toolbar says
			// it is being written. The focus ring in textarea.css is bound to `input` for that reason.
			plain: [],
		},
	},
});
